import { GameMode } from '../../../constants';
import type { GameWorld } from '../../../world/GameWorld';
import { nextGateTicks } from '../../../stage/gateCue';
import type { LayerStackSprite } from '../../layerstack/LayerStackSprite';
import { GATE_SLUG } from '../assets/registry';
import { WORLD_TO_R2 } from '../scale';
import type { EntityFamily, FamilyDeps, FrameContext } from './types';

/**
 * The calibration-gate structure in the world. Pure render-side staging driven
 * by sim signals (read-only): it slides in from the right while the HUD's
 * approach banner counts down, parks in BECKONING, switches to OPEN for the
 * shop, and rides the resumed scroll off the left edge.
 *
 * All positions are simulation units; the ×2.5 conversion happens at the
 * single setPixelPosition call.
 */
/** Parked centre, sim units — right third, clear of the ship's usual lane. */
const PARK_X = 300;
/** The platform base sits just above the playfield's bottom edge. */
const BASE_Y = 212;
const STATE_BECKONING = 'beckoning';
const STATE_OPEN = 'open';

export class GateFamily implements EntityFamily {
  private sprite: LayerStackSprite | null = null;
  /** scrollX at the parked position; drives the ride-out after the gate closes. */
  private parkedScrollX = 0;
  private parked = false;
  private seenPassSerial = 0;

  init(deps: FamilyDeps): void {
    this.sprite = deps.stacks.acquire(GATE_SLUG, deps.layer);
    this.sprite.visible = false;
  }

  onSceneReset(): void {
    this.parked = false;
    this.seenPassSerial = 0;
    if (this.sprite) this.sprite.visible = false;
  }

  sync(world: GameWorld, _frame: FrameContext): void {
    const sprite = this.sprite;
    if (!sprite) return;
    const halfWidth = sprite.targetWidth / (2 * WORLD_TO_R2);
    const offRight = 384 + halfWidth;

    const passSerial = world.stageDirector.gatePassSerial;
    if (world.continuousArsenalEnabled && passSerial !== this.seenPassSerial) {
      this.seenPassSerial = passSerial;
      this.parked = true;
      this.parkedScrollX = world.scrollX;
      this.place(sprite, PARK_X, STATE_OPEN);
      return;
    }

    if (world.mode === GameMode.CalibrationGate) {
      this.parked = true;
      this.parkedScrollX = world.scrollX;
      this.place(sprite, PARK_X, STATE_OPEN);
      return;
    }
    if (world.mode !== GameMode.Playing) {
      if (!this.parked) sprite.visible = false;
      return;
    }

    const ahead = nextGateTicks(world.stageDirector);
    const approachTicks = world.stageDirector.gateApproachTicks;
    if (ahead >= 0 && ahead <= approachTicks) {
      // Slide in with the countdown; also covers re-approach after a retry.
      this.parked = false;
      const progress = 1 - ahead / approachTicks;
      this.place(sprite, offRight + (PARK_X - offRight) * progress, STATE_BECKONING);
      return;
    }
    if (this.parked) {
      // Gate closed: the structure stays put in the world and the resumed
      // scroll carries it off the left edge. It stays OPEN — the ship departs
      // through the opened arch.
      const x = PARK_X - (world.scrollX - this.parkedScrollX);
      if (x < -halfWidth) {
        this.parked = false;
        sprite.visible = false;
        return;
      }
      this.place(sprite, x, STATE_OPEN);
      return;
    }
    sprite.visible = false;
  }

  private place(sprite: LayerStackSprite, simX: number, state: string | null): void {
    sprite.visible = true;
    sprite.setState(state);
    sprite.setPixelPosition(
      simX * WORLD_TO_R2 - sprite.targetWidth / 2,
      BASE_Y * WORLD_TO_R2 - sprite.targetHeight,
    );
  }

  dispose(): void {
    this.sprite = null;
    this.parked = false;
    this.seenPassSerial = 0;
  }
}
